import { useState, useCallback, useEffect, useRef } from "react";
import type { GameFacade } from "./GameFacade";
import { checkAchievements } from "@bastion/game-core";
import { useI18n } from "./i18n";

interface ResultScreenProps {
  facade: GameFacade;
  onClose: () => void;
}

/**
 * 胜利或失败后显示的游戏结果界面。
 *
 * - 在游戏期间订阅 'enemy_killed' 事件以累计击杀数
 *   （facade 不直接暴露 enemiesKilled；事件追踪是
 *   React 侧的职责——CLAUDE.md §8：UI 不得直接修改核心 GameState）。
 * - 游戏结束时读取最终快照并写入会话记录。
 * - 检查成就并显示新解锁成就的 toast 通知。
 * - 显示波次、金币、生命、会话统计和"再来一局"操作。
 */
export function ResultScreen({ facade, onClose }: ResultScreenProps) {
  const [killed, setKilled] = useState(0);
  const hasRecorded = useRef(false);
  const [newlyUnlocked, setNewlyUnlocked] = useState<string[]>([]);
  const [showToasts, setShowToasts] = useState(false);
  const { t, tAchievement } = useI18n();

  // 从实时事件流中累计敌人数。
  useEffect(() => {
    const unsub = facade.onEvent("enemy_killed", () => {
      setKilled((k) => k + 1);
    });
    return unsub;
  }, [facade]);

  const snapshot = facade.snapshotNow();
  const won = snapshot.status === "won";

  // 结果界面首次渲染时检查成就。
  useEffect(() => {
    const ctx: import("@bastion/game-core").AchievementContext = {
      enemiesKilled: killed,
      currentWave: snapshot.wave,
      towersBuiltTotal: facade.getTowersBuiltSession(),
      goldEarned: Math.max(0, snapshot.gold),
      finalWaveCompleted: snapshot.status === "won",
      livesLostThisGame: facade.getLivesLostThisGame(),
      wave1CompleteTime: facade.getWave1CompleteTime(),
      elapsedTime: facade.getElapsedTime(),
    };
    const newly = checkAchievements(ctx, facade.getTowersBuiltSession());
    if (newly.length > 0) {
      setNewlyUnlocked(newly);
      setShowToasts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 超时后关闭 toast 通知。
  useEffect(() => {
    if (!showToasts || newlyUnlocked.length === 0) return;
    const timer = window.setTimeout(() => setShowToasts(false), 5000);
    return () => window.clearTimeout(timer);
  }, [showToasts, newlyUnlocked]);

  // 恰好持久化一次结果。
  const recordAndClose = useCallback(() => {
    if (hasRecorded.current) return;
    hasRecorded.current = true;

    const finalSnapshot = facade.snapshotNow();
    const goldEarned = Math.max(0, finalSnapshot.gold);

    facade.recordGameResult({
      status: finalSnapshot.status,
      wave: finalSnapshot.wave,
      goldEarned,
      enemiesKilled: killed,
    });

    onClose();
  }, [facade, killed, onClose]);

  const stats = facade.getSessionStats();

  return (
    <div className="result-screen" data-testid="result-overlay">
      {/* Achievement unlock toasts */}
      {showToasts && newlyUnlocked.length > 0 && (
        <div className="achievement-toasts" data-testid="achievement-toasts">
          {newlyUnlocked.map((id) => {
            return (
              <div key={id} className="achievement-toast" data-testid={`achievement-toast-${id}`}>
                <span className="achievement-toast-icon">&#127942;</span>
                <span className="achievement-toast-text">
                  {t("result_unlocked", { name: tAchievement(id).name })}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="result-screen-inner">
        <h2
          className={`result-title ${won ? "result-victory" : "result-defeat"}`}
          data-testid="result-title"
        >
          {won ? t("result_victory") : t("result_defeat")}
        </h2>

        <div className="result-stats" data-testid="result-stats">
          <div className="result-stat-row">
            <span className="result-stat-label">{t("result_wave_reached")}</span>
            <span className="result-stat-value">
              {snapshot.wave}
              {snapshot.isEndless ? (
                <span className="result-stat-sub"> ∞</span>
              ) : (
                <span className="result-stat-sub">/ {snapshot.totalWaves}</span>
              )}
            </span>
          </div>
          <div className="result-stat-row">
            <span className="result-stat-label">{t("result_gold_remaining")}</span>
            <span className="result-stat-value result-gold">{snapshot.gold}</span>
          </div>
          <div className="result-stat-row">
            <span className="result-stat-label">{t("result_lives_remaining")}</span>
            <span className="result-stat-value result-lives">
              {snapshot.lives}
              <span className="result-stat-sub">/ {snapshot.maxLives}</span>
            </span>
          </div>
          <div className="result-stat-row">
            <span className="result-stat-label">{t("result_enemies_killed")}</span>
            <span className="result-stat-value">{killed}</span>
          </div>
        </div>

        {stats.gamesPlayed > 0 && (
          <div className="result-session-stats" data-testid="session-stats">
            <h3 className="result-section-title">{t("result_session_stats")}</h3>
            <div className="result-stats-grid">
              <div className="result-stat-tile">
                <span className="result-tile-label">{t("result_games_played")}</span>
                <span className="result-tile-value">{stats.gamesPlayed}</span>
              </div>
              <div className="result-stat-tile">
                <span className="result-tile-label">{t("result_wins")}</span>
                <span className="result-tile-value result-victory-text">{stats.totalWins}</span>
              </div>
              <div className="result-stat-tile">
                <span className="result-tile-label">{t("result_losses")}</span>
                <span className="result-tile-value result-defeat-text">{stats.totalLosses}</span>
              </div>
              <div className="result-stat-tile">
                <span className="result-tile-label">{t("result_avg_wave")}</span>
                <span className="result-tile-value">{stats.avgWaveReached.toFixed(1)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="result-actions">
          <button
            className="result-btn primary"
            data-testid="play-again-btn"
            onClick={recordAndClose}
          >
            {t("result_play_again")}
          </button>
          <button className="result-btn secondary" data-testid="main-menu-btn" onClick={onClose}>
            {t("result_main_menu")}
          </button>
        </div>
      </div>
    </div>
  );
}
